/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {TechnicianGroupModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/technician-group.model';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {InputListModel} from '../input-list.model';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {ValidatorFn, Validators} from '@angular/forms';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {ChipInputValueModel} from '../../../../shared/chip-input/models/chip-input-value.model';
import {ChipInputComponent} from '../../../../shared/chip-input/chip-input.component';
import {TooltipEnum} from '../../../../shared/tooltip/tooltip.enum';
import {take} from 'rxjs/operators';
import {timer} from 'rxjs';
import { v4 as uuid } from 'uuid';
import {TreeNodeModel} from '../../models/tree-node-model';
import {ChipInputConfigurationModel} from '../../../../shared/chip-input/models/chip-input-configuration.model';

@Component({
    selector: 'app-technician-group-modal',
    templateUrl: './technician-group-modal.component.html',
    styleUrls: ['../business-settings-modal.css', './technician-group-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TechnicianGroupModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<TechnicianGroupModel>> {

    @ViewChild('name', {static: true}) nameInput: InputTextComponent;
    @ViewChild('modelCodes') modelCodesInput: ChipInputComponent<string>;

    readonly ADD_SUBMIT_TITLE = 'Add';
    readonly ADD_TITLE = 'Add a technician group';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly EDIT_TITLE = 'Edit technician group';
    readonly ENTITY_NAME = 'technician group';
    public nameInputMessages = new Map<WarningsEnum, string[]>();

    TooltipEnum = TooltipEnum;

    public modelCodeArrayIsValid = true;
    readonly invalidActionText = 'Not a valid action for the existing connections.';

    public modelCodesInputConfiguration: ChipInputConfigurationModel<string> = {
        initialChips: [],
        excludedChipValues: [],
        chipLabel: 'model code',
        groupLabel: 'group',
        maxChipsLimit: 50,
        messageErrorExample: 'A01',
        chipTextMaxLength: 3,
        chipInputValidator: [Validators.maxLength(3), Validators.pattern(/^(?:[A-Za-z]\d{2}|[A-Za-z]{2}\d)$/)]
    };

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,20}$/)]
    };

    constructor(private inputMessagesService: InputMessagesService) {
        super(inputMessagesService);
    }

    getInitialData(): void {
        this.setInitialModelCodesInputConfiguration();
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: uuid()
            }
        });
    }

    setEntity(element: EntityElementModel<TechnicianGroupModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: {...element.currentValue}
        });
        this.onReady.pipe(take(1))
                .subscribe(() => {
                    timer(0)
                            .subscribe(() => {
                                this.nameInput.setValue(this.state.currentValue.name);
                                this.setInitialModelCodesInputConfiguration();
                                this.setActiveInputState(element.active, true);
                            });
                });
    }

    nameInputChange(name: string): void {
        const nameInUpperCase = name != null ? name.toUpperCase() : null;

        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameInputMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.nameInput.inputControl, this.nameInputMessages, this.nameInput.label);
        this.nameInputMessages = new Map(this.nameInputMessages);
        const errors = this.nameInputMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsName(nameInUpperCase, this.ENTITY_NAME, this.nameInputMessages, this.nameInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: nameInUpperCase
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    modelCodesInputChange(value: ChipInputValueModel<string>): void {
        this.manageModelCodeArrayValidity(value?.chips);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                modelCodes: value.chips
            },
            invalid: {
                ...this.state.invalid,
                modelCodes: !value.isValid
            }
        });
        this.setModalValidity();
    }

    setModalValidity(isSetInitialValue?: boolean): void {
        super.setModalValidity(isSetInitialValue);
        this.isValid = this.isValid && !this.state?.invalid?.modelCodes && this.modelCodeArrayIsValid;
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }];
    }

    private manageModelCodeArrayValidity(modelCodes: string[]): void {
        if (this.isEdit && this.state.originalValue != null
                && (this.lastModelCodeWasRemoved(modelCodes) || this.firstModelCodeWasAdded(modelCodes))) {
            this.modelCodeArrayIsValid = this.canEditConnectionsValidation(
                    this.state.originalValue.code, this.configurationTreeNode, modelCodes);
        }
    }

    private lastModelCodeWasRemoved(modelCodes: string[]): boolean {
        return modelCodes?.length === 0 && this.state.originalValue?.modelCodes?.length > 0;
    }

    private firstModelCodeWasAdded(modelCodes: string[]): boolean {
        return modelCodes?.length === 1 && this.state.currentValue?.modelCodes?.length === 0;
    }

    private canEditConnectionsValidation = (originalCode: string, treeNode: TreeNodeModel, modelCodes: string[]): boolean => {
        let canEdit = true;
        if (treeNode.code != null && treeNode.code === originalCode) {
            canEdit = treeNode.relationTree?.length === 1 || modelCodes.length > 0;
        }
        if (canEdit && treeNode.relationTree != null) {
            for (let i = 0; i < treeNode.relationTree.length; i++) {
                if (treeNode.relationTree[i].code === originalCode) {
                    canEdit = treeNode.relationTree.length === 1 || modelCodes.length > 0;
                    break;
                }
                if (canEdit && treeNode.relationTree?.length > 0) {
                    canEdit = this.canEditConnectionsValidation(originalCode, treeNode.relationTree[i], modelCodes);
                }
            }
        }
        return canEdit;
    };

    private setInitialModelCodesInputConfiguration(): void {
        this.modelCodesInputConfiguration = {
            ...this.modelCodesInputConfiguration,
            initialChips: this.state.currentValue?.modelCodes != null ? this.state.currentValue.modelCodes : [],
            excludedChipValues: this.excludedModelCodes()
        };
    }

    private excludedModelCodes(): string[] {
        const array: string[] = [];
        const otherEntities = this.excludedEntities
                .filter((entity) => entity.code !== this.state.currentValue?.code);

        otherEntities.forEach((entity) => {
            if (entity?.currentValue?.modelCodes?.length > 0) {
                array.push(entity.currentValue.modelCodes);
            }
        });
        return array.flat();
    }
}
